import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { labFetch } from './labSession';
import defaults from '../../../shared/product-limits.json';
import fieldDefinitions from '../../../shared/product-limit-fields.json';

export type ProductLimits = typeof defaults;
export type ProductLimitKey = keyof ProductLimits;
export const DEFAULT_LIMITS: Readonly<ProductLimits> = Object.freeze({ ...defaults });
export const textLength = (value: string) => Array.from(value).length;
/** A lowered policy allows owners to keep or shorten their saved words. */
export const textWithinLimit = (value: string, maximum: number, previous = '') => textLength(value) <= Math.max(maximum, textLength(previous));
export const PRODUCT_LIMIT_FIELDS = fieldDefinitions as Array<{ key: ProductLimitKey; group: string; label: string; min: number; max: number; guidance: boolean }>;
export interface ProductLimitsResponse { version: number; revision: number; limits: ProductLimits; updatedAt: string | null }
export interface ProductLimitsHistory { revision: number; updatedAt: string; actorId: string; action: 'save' | 'restore'; changes: Partial<Record<ProductLimitKey, { before: number; after: number }>> }
export interface ProductLimitsAdminResponse extends ProductLimitsResponse {
  defaults: ProductLimits;
  fields: typeof PRODUCT_LIMIT_FIELDS;
  storage: 'defaults' | 'durable';
  writable: boolean;
  history: ProductLimitsHistory[];
  persisted?: boolean;
}
export class ProductLimitsError extends Error {
  constructor(message: string, public status: number, public code?: string) { super(message); }
}
function assertResponse(value: ProductLimitsResponse): ProductLimitsResponse {
  if (value?.version !== 1 || !Number.isSafeInteger(value.revision) || value.revision < 0
    || !value.limits || PRODUCT_LIMIT_FIELDS.some(field => !Number.isSafeInteger(value.limits[field.key])
      || value.limits[field.key] < field.min || value.limits[field.key] > field.max)) throw new Error('The deployment limits response is invalid.');
  return value;
}
async function request<T extends ProductLimitsResponse>(url: string, init?: RequestInit): Promise<T> {
  const response = await (url.startsWith('/api/lab/') ? labFetch : fetch)(url, { credentials: 'same-origin', cache: 'no-store', ...init });
  const value = await response.json().catch(() => ({}));
  if (!response.ok) throw new ProductLimitsError(value.error || 'Deployment limits could not be loaded.', response.status, value.code);
  assertResponse(value);
  return value as T;
}
type Snapshot = { limits: Readonly<ProductLimits>; revision: number; loading: boolean; error: string | null };
let snapshot: Snapshot = { limits: DEFAULT_LIMITS, revision: 0, loading: true, error: null };
let pending: Promise<ProductLimitsResponse> | null = null;
let accepted: ProductLimitsResponse | null = null;
let bootstrap: Promise<unknown> | null = null;
let initial: Promise<ProductLimitsResponse> | null = null;
/** The boot config/session already carries policy; mounted readers join it. */
export function bootstrapProductLimits(config: Promise<unknown>) { bootstrap = config.catch(() => undefined); }
export function ensureProductLimits(): Promise<ProductLimitsResponse> {
  if (accepted) return Promise.resolve(accepted);
  if (!initial) initial = (bootstrap ?? Promise.resolve()).then(() => accepted ?? fetchProductLimits());
  return initial;
}
const listeners = new Set<() => void>();
function publish(next: Snapshot) { snapshot = next; for (const listener of listeners) listener(); }
export function acceptProductLimits(value: ProductLimitsResponse) {
  assertResponse(value);
  if (value.revision < snapshot.revision) return;
  accepted = value;
  const changed = value.revision !== snapshot.revision;
  publish({ limits: Object.freeze({ ...value.limits }), revision: value.revision, loading: false, error: null });
  if (changed && typeof window !== 'undefined') window.dispatchEvent(new Event('kalibrLimitsChanged'));
}
export function fetchProductLimits(): Promise<ProductLimitsResponse> {
  if (!pending) {
    pending = request<ProductLimitsResponse>('/api/product-limits').then(value => { acceptProductLimits(value); return value; })
      .catch(error => { publish({ ...snapshot, loading: false, error: error instanceof Error ? error.message : 'Deployment limits could not be loaded.' }); throw error; })
      .finally(() => { pending = null; });
  }
  return pending;
}
let stopRefreshing: (() => void) | null = null;
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (!stopRefreshing && typeof window !== 'undefined') {
    const load = () => { void fetchProductLimits().catch(() => undefined); };
    window.addEventListener('focus', load);
    const interval = window.setInterval(() => { if (document.visibilityState !== 'hidden') load(); }, 30_000);
    stopRefreshing = () => { window.removeEventListener('focus', load); window.clearInterval(interval); stopRefreshing = null; };
  }
  return () => { listeners.delete(listener); if (!listeners.size) stopRefreshing?.(); };
};
const getSnapshot = () => snapshot;
export function useProductLimits() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const refresh = useCallback(() => fetchProductLimits(), []);
  useEffect(() => {
    void ensureProductLimits().catch(() => undefined);
  }, [refresh]);
  return { ...state, refresh };
}
export const fetchProductLimitsAdmin = () => request<ProductLimitsAdminResponse>('/api/lab/dev/product-limits');
export async function saveProductLimits(limits: ProductLimits, expectedRevision: number, restoreDefaults = false) {
  const result = await request<ProductLimitsAdminResponse>('/api/lab/dev/product-limits', {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limits, expectedRevision, restoreDefaults }),
  });
  if (result.persisted !== true) throw new Error('The server did not confirm a durable policy save. Your edits are still here.');
  acceptProductLimits(result);
  return result;
}
